import { useMemo, useState } from 'react'
import localStorage from '@locmod/local-storage'
import { type GameMarkets } from '@azuro-org/toolkit'
import { constants } from 'helpers'

const useCollapse = (markets: GameMarkets) => {
  const getInitialCollapsedMarkets = (): string[] => {
    try {
      const stored = localStorage.getItem(
        constants.localStorageKeys.collapsedMarkets
      )
      if (stored && typeof stored === 'string') {
        return JSON.parse(stored)
      }
      return []
    } catch (error) {
      console.error(
        'Failed to parse collapsedMarkets from localStorage:',
        error
      )
      return []
    }
  }

  const initialCollapsedMarkets: string[] = getInitialCollapsedMarkets()

  const [collapsedMarketIds, setCollapsedMarketIds] = useState(
    initialCollapsedMarkets
  )

  const marketIds = useMemo<string[]>(() => {
    if (!markets?.length) {
      return []
    }

    return markets.map(({ marketKey }) => marketKey)
  }, [markets])

  const areAllCollapsed = !marketIds.some(
    marketId => !collapsedMarketIds.includes(marketId)
  )

  const collapse = (marketId: string) => {
    let newCollapsed: string[]
    const isCollapse = !collapsedMarketIds.includes(marketId)

    if (isCollapse) {
      newCollapsed = [...collapsedMarketIds, marketId]
    } else {
      newCollapsed = collapsedMarketIds.filter(market => market !== marketId)
    }

    localStorage.setItem(
      constants.localStorageKeys.collapsedMarkets,
      JSON.stringify(newCollapsed)
    )
    setCollapsedMarketIds(newCollapsed)
  }

  const collapseAll = () => {
    let newCollapsed: string[] = []

    if (!areAllCollapsed) {
      newCollapsed = marketIds
    }

    localStorage.setItem(
      constants.localStorageKeys.collapsedMarkets,
      JSON.stringify(newCollapsed)
    )

    setCollapsedMarketIds(newCollapsed)
  }

  return { areAllCollapsed, collapsedMarketIds, collapse, collapseAll }
}

const useOvertimeCollapse = (
  groupedChildMarkets: Record<string, any[]>,
  mainMarketTypeId: number
) => {
  const getInitialCollapsedMarkets = (): string[] => {
    try {
      const stored = localStorage.getItem(
        constants.localStorageKeys.collapsedMarkets
      )
      if (stored && typeof stored === 'string') {
        return JSON.parse(stored)
      }
      return []
    } catch (error) {
      console.error(
        'Failed to parse collapsedMarkets from localStorage:',
        error
      )
      return []
    }
  }

  const initialCollapsedMarkets: string[] = getInitialCollapsedMarkets()

  const [collapsedMarketIds, setCollapsedMarketIds] = useState(
    initialCollapsedMarkets
  )

  const marketIds = useMemo<string[]>(() => {
    const ids: string[] = [mainMarketTypeId.toString()]

    if (groupedChildMarkets) {
      Object.keys(groupedChildMarkets).forEach(key => {
        ids.push(key)
      })
    }

    return ids
  }, [groupedChildMarkets, mainMarketTypeId])

  const areAllCollapsed = !marketIds.some(
    marketId => !collapsedMarketIds.includes(marketId)
  )

  const collapse = (marketId: string) => {
    let newCollapsed: string[]
    const isCollapse = !collapsedMarketIds.includes(marketId)

    if (isCollapse) {
      newCollapsed = [...collapsedMarketIds, marketId]
    } else {
      newCollapsed = collapsedMarketIds.filter(market => market !== marketId)
    }

    localStorage.setItem(
      constants.localStorageKeys.collapsedMarkets,
      JSON.stringify(newCollapsed)
    )
    setCollapsedMarketIds(newCollapsed)
  }

  const collapseAll = () => {
    let newCollapsed: string[] = []

    if (!areAllCollapsed) {
      newCollapsed = marketIds
    }

    localStorage.setItem(
      constants.localStorageKeys.collapsedMarkets,
      JSON.stringify(newCollapsed)
    )

    setCollapsedMarketIds(newCollapsed)
  }

  return { areAllCollapsed, collapsedMarketIds, collapse, collapseAll }
}

export default useCollapse
export { useOvertimeCollapse }
